import {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {getYYYYMMFormat} from '@di-shared/api/di/utils';
import {ERROR} from '@di-shared/const';
import {emitEventLog} from '@di-shared/features/analytics';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {DrivingInfoHeaderProps} from '@di-shared/features/history/driving-info/DrivingInfoHeader/index';
import useDrivingInfoKr from '@di-shared/features/history/driving-info/useDrivingInfo.kr';
import {ROUTE_PATHS} from '@di-shared/routes';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {endDateOfMonth, isSameYearAndMonth, startDateOfMonth} from '@shared/dateUtils';

export default function ({vin, selectedDate, setSelectedDate, changeMonth}: DrivingInfoHeaderProps) {
  return (
    <ErrorBoundary fallback={<FallbackError selectedDate={selectedDate} />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner
          key={getYYYYMMFormat(selectedDate)}
          vin={vin}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          changeMonth={changeMonth}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, selectedDate, setSelectedDate, changeMonth}: DrivingInfoHeaderProps) {
  const {t} = useInferableTranslation();
  const {verifiedDrivingDistances} = useDrivingInfoKr({vin});
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const today = new Date();
  const updatedDate = isSameYearAndMonth(today, selectedDate) ? today : endDateOfMonth(selectedDate);

  async function openSelectMonthBottomSheet() {
    const reverseMonths = [...verifiedDrivingDistances.map((driving) => driving.verifiedDate)].reverse();
    const date = await ccWebviewUtils.openBottomSheetWebViewUrl<Date>(ROUTE_PATHS.BOTTOM_SHEET_HISTORY_SELECT_MONTH.getFullPath(), {
      months: reverseMonths,
      selectedDate,
    });

    if (!date) {
      return;
    }

    setSelectedDate(date);
    changeMonth(date);
  }

  return (
    <>
      <div className="section_heading type_main">
        <button
          type="button"
          className="btn_monthly body_medium_b"
          onClick={() => {
            emitEventLog({
              event: 'di_driveinfo_interaction',
              data_action: 'select month',
              data_name: t('T10003'),
            });
            openSelectMonthBottomSheet();
          }}
        >
          {CldrDatetimeFormatter.format(selectedDate, 'yMMM')}
          <span
            className="ic ic_arrow_down_16px"
            aria-hidden="true"
          />
        </button>
        <h3 className="section_heading_title">{t('T10003')}</h3>
        <span className="section_heading_caption">{t('T10004_$date', {date: CldrDatetimeFormatter.format(updatedDate, 'yMMMd')})}</span>
      </div>
    </>
  );
}

function FallbackLoading() {
  return (
    <>
      <div className="section_heading type_main">
        <button
          type="button"
          className="btn_monthly body_medium_b skeleton"
          style={{width: '160px', height: '20px'}}
        />
        <h3
          className="section_heading_title skeleton"
          style={{width: '200px', height: '28px'}}
        />
        <span
          className="section_heading_caption skeleton"
          style={{width: '160px', height: '20px'}}
        />
      </div>
    </>
  );
}

function FallbackError({selectedDate}: {selectedDate: Date}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const startDate = startDateOfMonth(selectedDate);

  return (
    <>
      <div className="section_heading type_main">
        <button
          type="button"
          className="btn_monthly body_medium_b"
        >
          {CldrDatetimeFormatter.format(startDate, 'yMMM')}
          <span
            className="ic ic_arrow_down_16px"
            aria-hidden="true"
          />
        </button>
        <h3 className="section_heading_title">{t('T10003')}</h3>
        <span className="section_heading_caption">{t('T10004_$date', {date: ERROR.DASH})}</span>
      </div>
    </>
  );
}
